/**
 * Markdown 入力補助の純粋ロジック。
 *
 * ここは「文字列 ＋ 選択範囲」を受け取り「置換すべき範囲と挿入文字列」を返すだけで、
 * DOM にも CodeMirror にも textarea にも触れない。両エディタは EditorAdapter を通じて
 * この結果を適用するだけなので、挙動が構造的に一致する（docs/03-editor.md §3）。
 *
 * IME について: これらの関数は composition 中に**呼ばれないこと**が呼び出し側の責務。
 * ここでは composition の判定を行わない（判定の在処を一箇所に保つため）。
 */

export interface TextState {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

/** 適用すべき単一の置換。`insert` を [from, to) へ入れ、選択を selection へ移す。 */
export interface TextEdit {
  from: number;
  to: number;
  insert: string;
  selection: { start: number; end: number };
}

/** インデント 1 段の幅。Markdown のネストリストとして安全な 2 スペース。 */
export const INDENT_UNIT = '  ';

/**
 * 行頭のリストマーカー。
 * 例: '  - ', '* ', '+ ', '1. ', '12) ', '> '
 */
interface ListMarker {
  /** マーカーを含む行頭からの前置き空白。 */
  indent: string;
  /** マーカー本体（区切りの空白を含む。例 '- ' '3. ' '> '）。 */
  marker: string;
  kind: 'bullet' | 'ordered' | 'quote';
  /** ordered のときの番号。 */
  ordinal: number;
  /** マーカーより後ろの本文。 */
  content: string;
}

const BULLET_RE = /^([ \t]*)([-*+])([ \t]+)(.*)$/;
const ORDERED_RE = /^([ \t]*)(\d{1,9})([.)])([ \t]+)(.*)$/;
const QUOTE_RE = /^([ \t]*)(>)([ \t]*)(.*)$/;

function parseListMarker(line: string): ListMarker | null {
  const ordered = ORDERED_RE.exec(line);
  if (ordered) {
    const [, indent, digits, delim, space, content] = ordered as unknown as string[];
    return {
      indent: indent!,
      marker: `${digits}${delim}${space}`,
      kind: 'ordered',
      ordinal: Number(digits),
      content: content!,
    };
  }
  const bullet = BULLET_RE.exec(line);
  if (bullet) {
    const [, indent, ch, space, content] = bullet as unknown as string[];
    return {
      indent: indent!,
      marker: `${ch}${space}`,
      kind: 'bullet',
      ordinal: 0,
      content: content!,
    };
  }
  const quote = QUOTE_RE.exec(line);
  if (quote) {
    const [, indent, ch, space, content] = quote as unknown as string[];
    return {
      indent: indent!,
      // 引用は '> ' に正規化する（'>' 直後に空白がない書き方も継続できるように）。
      marker: `${ch}${space!.length > 0 ? space : ' '}`,
      kind: 'quote',
      ordinal: 0,
      content: content!,
    };
  }
  return null;
}

/** pos を含む行の [開始, 終了) を返す（終了は改行文字を含まない）。 */
export function lineBoundsAt(value: string, pos: number): { start: number; end: number } {
  const clamped = Math.max(0, Math.min(pos, value.length));
  const start = value.lastIndexOf('\n', clamped - 1) + 1;
  const nl = value.indexOf('\n', clamped);
  return { start, end: nl === -1 ? value.length : nl };
}

/**
 * Enter によるリスト記号の自動継続。
 *
 * - リスト行の末尾で Enter → 次行へ同じマーカー（番号付きは +1）を継続
 * - 空のリスト項目で Enter → マーカーを除去してリストを解除（改行は挿入しない）
 * - リスト行でない、または継続すべきでない場合 → null（ネイティブの改行に任せる）
 *
 * 呼び出し側は `beforeinput`（inputType が insertLineBreak / insertParagraph）で
 * 検知し、null 以外が返ったときだけ preventDefault してこの編集を適用する
 * （docs/03-editor.md §4-3）。
 */
export function continueListOnEnter(state: TextState): TextEdit | null {
  const { value, selectionStart, selectionEnd } = state;
  // 選択範囲があるときは継続しない（選択削除＋改行はネイティブに任せる）。
  if (selectionStart !== selectionEnd) return null;

  const { start: lineStart } = lineBoundsAt(value, selectionStart);
  // カーソルより前の部分だけでマーカーを判定する。マーカーの途中で Enter された場合に
  // 継続してしまうのを防ぐ。
  const beforeCursor = value.slice(lineStart, selectionStart);
  const parsed = parseListMarker(beforeCursor);
  if (!parsed) return null;

  // マーカー途中（`-` と空白の間など）にカーソルがある場合は、上の parseListMarker が
  // beforeCursor に対して失敗するため、ここへ到達した時点でカーソルはマーカーの後ろにある。

  const { end: lineEnd } = lineBoundsAt(value, selectionStart);
  const restOfLine = value.slice(selectionStart, lineEnd);

  // 空のリスト項目（マーカーだけで本文がない）→ リスト解除。
  if (parsed.content.trim() === '' && restOfLine.trim() === '') {
    // ネストしている場合は 1 段だけ浅くして、深い階層から一度に抜けないようにする。
    if (parsed.indent.length > 0) {
      const outdented = removeOneIndent(parsed.indent);
      const replacement = outdented + parsed.marker;
      return {
        from: lineStart,
        to: lineEnd,
        insert: replacement,
        selection: { start: lineStart + replacement.length, end: lineStart + replacement.length },
      };
    }
    return {
      from: lineStart,
      to: lineEnd,
      insert: '',
      selection: { start: lineStart, end: lineStart },
    };
  }

  const nextMarker =
    parsed.kind === 'ordered'
      ? `${parsed.ordinal + 1}${parsed.marker.slice(String(parsed.ordinal).length)}`
      : parsed.marker;
  const insert = `\n${parsed.indent}${nextMarker}`;
  const caret = selectionStart + insert.length;
  return {
    from: selectionStart,
    to: selectionStart,
    insert,
    selection: { start: caret, end: caret },
  };
}

/** 行頭インデントから 1 段（最大 INDENT_UNIT 幅、タブなら 1 文字）取り除く。 */
function removeOneIndent(indent: string): string {
  if (indent.endsWith('\t')) return indent.slice(0, -1);
  let remove = 0;
  while (remove < INDENT_UNIT.length && indent.endsWith(' '.repeat(remove + 1))) {
    remove++;
  }
  return remove > 0 ? indent.slice(0, indent.length - remove) : indent;
}

/**
 * 選択行（複数行可）のインデント／アウトデント。
 *
 * アクセサリバーのインデントボタンと、複数行選択でのまとめて操作の両方がこれを使う
 * （docs/01-requirements.md §5.3、docs/03-editor.md §3）。
 *
 * @param dir 1 でインデント、-1 でアウトデント
 * @returns 変更が生じない場合は null
 */
export function indentSelection(state: TextState, dir: 1 | -1): TextEdit | null {
  const { value } = state;
  const selStart = Math.min(state.selectionStart, state.selectionEnd);
  const selEnd = Math.max(state.selectionStart, state.selectionEnd);

  const first = lineBoundsAt(value, selStart);
  // 選択が改行直後の位置で終わっている場合、その行は対象に含めない。
  const lastPos = selEnd > selStart && value[selEnd - 1] === '\n' ? selEnd - 1 : selEnd;
  const last = lineBoundsAt(value, lastPos);

  const block = value.slice(first.start, last.end);
  const lines = block.split('\n');

  let firstLineDelta = 0;
  let totalDelta = 0;
  const nextLines = lines.map((line, i) => {
    let next: string;
    if (dir === 1) {
      // 空行にはインデントを足さない（末尾に意味のない空白を残さないため）。
      next = line.length === 0 ? line : INDENT_UNIT + line;
    } else {
      const match = /^[ \t]*/.exec(line)![0];
      next = removeOneIndent(match) + line.slice(match.length);
    }
    const delta = next.length - line.length;
    if (i === 0) firstLineDelta = delta;
    totalDelta += delta;
    return next;
  });

  const nextBlock = nextLines.join('\n');
  if (nextBlock === block) return null;

  // 選択範囲は「操作前と同じテキストを覆う」ように動かす。
  // 先頭行の削れ量ぶんだけ開始位置を、全行の合計ぶんだけ終了位置をずらす。
  const nextStart = Math.max(first.start, selStart + firstLineDelta);
  const nextEnd = Math.max(nextStart, selEnd + totalDelta);

  return {
    from: first.start,
    to: last.end,
    insert: nextBlock,
    selection: { start: nextStart, end: nextEnd },
  };
}

/** 選択範囲が覆う「行の範囲」。行単位で編集する操作が共通して使う。 */
interface SelectedLines {
  /** 対象行全体の開始位置。 */
  from: number;
  /** 対象行全体の終了位置（末尾の改行を含まない）。 */
  to: number;
  lines: string[];
}

function selectedLines(state: TextState): SelectedLines {
  const { value } = state;
  const selStart = Math.min(state.selectionStart, state.selectionEnd);
  const selEnd = Math.max(state.selectionStart, state.selectionEnd);
  const first = lineBoundsAt(value, selStart);
  // 選択が改行直後の位置で終わっている場合、その行は対象に含めない。
  const lastPos = selEnd > selStart && value[selEnd - 1] === '\n' ? selEnd - 1 : selEnd;
  const last = lineBoundsAt(value, lastPos);
  return {
    from: first.start,
    to: last.end,
    lines: value.slice(first.start, last.end).split('\n'),
  };
}

/**
 * 行ごとの変換結果から TextEdit を組み立てる。
 * 選択範囲は「操作前と同じテキストを覆う」ように動かす。
 */
function lineEdit(
  state: TextState,
  range: SelectedLines,
  nextLines: string[],
): TextEdit | null {
  const block = range.lines.join('\n');
  const nextBlock = nextLines.join('\n');
  if (nextBlock === block) return null;

  const selStart = Math.min(state.selectionStart, state.selectionEnd);
  const selEnd = Math.max(state.selectionStart, state.selectionEnd);
  const firstLineDelta = (nextLines[0]?.length ?? 0) - (range.lines[0]?.length ?? 0);
  const totalDelta = nextBlock.length - block.length;

  const nextStart = Math.max(range.from, selStart + firstLineDelta);
  const nextEnd = Math.max(nextStart, selEnd + totalDelta);
  return {
    from: range.from,
    to: range.to,
    insert: nextBlock,
    selection: { start: nextStart, end: nextEnd },
  };
}

/**
 * 選択行を箇条書きにする／解除する（アクセサリバーの「リスト化」）。
 * 対象行がすべて既に箇条書きなら解除、そうでなければ全行へ `- ` を付ける。
 */
export function toggleBulletList(state: TextState): TextEdit | null {
  const { value } = state;
  const selStart = Math.min(state.selectionStart, state.selectionEnd);
  const selEnd = Math.max(state.selectionStart, state.selectionEnd);

  const first = lineBoundsAt(value, selStart);
  const lastPos = selEnd > selStart && value[selEnd - 1] === '\n' ? selEnd - 1 : selEnd;
  const last = lineBoundsAt(value, lastPos);

  const lines = value.slice(first.start, last.end).split('\n');
  const meaningful = lines.filter((l) => l.trim() !== '');
  const allBullets =
    meaningful.length > 0 &&
    meaningful.every((l) => {
      const m = parseListMarker(l);
      return m?.kind === 'bullet';
    });

  // 空行を飛ばすのは「複数行選択のとき、段落の区切りにマーカーを付けない」ため。
  // 対象がすべて空行なら話が別で、それは「新しい行で箇条書きを始めたい」という
  // 最も普通の操作。ここを飛ばすとアクセサリバーのリスト化ボタンが何もしない
  // （2026-07-27 に実機で発覚）。
  const skipBlankLines = meaningful.length > 0;

  let firstLineDelta = 0;
  let totalDelta = 0;
  const nextLines = lines.map((line, i) => {
    let next = line;
    const parsed = line.trim() === '' ? null : parseListMarker(line);
    if (line.trim() === '' && skipBlankLines) {
      // 空行はそのまま
    } else if (allBullets) {
      next = parsed!.indent + parsed!.content;
    } else if (parsed?.kind === 'bullet') {
      // 既に箇条書きの行はそのまま。ここを素通りさせるとマーカーが二重になる。
      next = line;
    } else if (parsed?.kind === 'ordered') {
      // 番号付きリストは箇条書きへ置き換える（マーカーを重ねない）。
      next = `${parsed.indent}- ${parsed.content}`;
    } else {
      const indent = /^[ \t]*/.exec(line)![0];
      next = `${indent}- ${line.slice(indent.length)}`;
    }
    const delta = next.length - line.length;
    if (i === 0) firstLineDelta = delta;
    totalDelta += delta;
    return next;
  });

  const nextBlock = nextLines.join('\n');
  if (nextBlock === value.slice(first.start, last.end)) return null;

  const nextStart = Math.max(first.start, selStart + firstLineDelta);
  const nextEnd = Math.max(nextStart, selEnd + totalDelta);
  return {
    from: first.start,
    to: last.end,
    insert: nextBlock,
    selection: { start: nextStart, end: nextEnd },
  };
}

// 行頭の空白を許容する。BULLET_RE / QUOTE_RE と揃えていないと、インデントされた
// 見出しを「見出しでない」と誤判定して `# # foo` のようにマーカーが二重になる。
const HEADING_RE = /^[ \t]*(#{1,6})[ \t]+(.*)$/;
/** 見出しボタンの巡回順。3 の次は「なし」へ戻る。 */
const MAX_HEADING_LEVEL = 3;

/**
 * 見出しレベルを巡回させる（アクセサリバーの「見出し」）。
 * `なし → # → ## → ### → なし`。スマホのバーに H1/H2/H3 を並べる余地がないため
 * 1 ボタンの巡回にしている（ADR-024）。
 *
 * 複数行が選択されている場合は、最初の意味のある行のレベルを基準に全行を揃える。
 */
export function cycleHeading(state: TextState): TextEdit | null {
  const range = selectedLines(state);
  const target = range.lines.find((l) => l.trim() !== '') ?? range.lines[0] ?? '';
  const current = HEADING_RE.exec(target);
  const currentLevel = current ? current[1]!.length : 0;
  const nextLevel = currentLevel >= MAX_HEADING_LEVEL ? 0 : currentLevel + 1;

  const hasMeaningful = range.lines.some((l) => l.trim() !== '');
  const nextLines = range.lines.map((line) => {
    if (line.trim() === '' && hasMeaningful) return line;
    const m = HEADING_RE.exec(line);
    const body = m ? m[2]! : line.replace(/^[ \t]+/, '');
    if (nextLevel === 0) return body;
    return `${'#'.repeat(nextLevel)} ${body}`;
  });
  return lineEdit(state, range, nextLines);
}

/**
 * 引用のトグル（アクセサリバーの「引用」）。
 * 対象行がすべて引用なら解除、そうでなければ全行へ `> ` を付ける。
 */
export function toggleQuote(state: TextState): TextEdit | null {
  const range = selectedLines(state);
  const meaningful = range.lines.filter((l) => l.trim() !== '');
  const allQuotes =
    meaningful.length > 0 && meaningful.every((l) => parseListMarker(l)?.kind === 'quote');
  // すべて空行なら「新しい行で引用を始めたい」操作とみなす（箇条書きと同じ理由）。
  const skipBlankLines = meaningful.length > 0;

  const nextLines = range.lines.map((line) => {
    if (line.trim() === '' && skipBlankLines) return line;
    const parsed = line.trim() === '' ? null : parseListMarker(line);
    if (allQuotes && parsed?.kind === 'quote') return parsed.indent + parsed.content;
    if (parsed?.kind === 'quote') return line; // マーカーを重ねない
    const indent = /^[ \t]*/.exec(line)![0];
    return `${indent}> ${line.slice(indent.length)}`;
  });
  return lineEdit(state, range, nextLines);
}

/**
 * 選択行をコードブロック（フェンス）で囲む／外す。
 * 既に前後がフェンスなら外し、そうでなければ ``` で挟む。
 */
export function toggleCodeFence(state: TextState): TextEdit | null {
  const range = selectedLines(state);
  const lines = range.lines;
  const isFence = (l: string | undefined) => l !== undefined && /^\s*```/.test(l);

  if (lines.length >= 2 && isFence(lines[0]) && isFence(lines[lines.length - 1])) {
    return lineEdit(state, range, lines.slice(1, -1));
  }
  return lineEdit(state, range, ['```', ...lines, '```']);
}

/**
 * 水平線を独立した行として挿入する。
 * 現在行が空ならその行を `---` にし、文字があれば次の行に足す。
 */
export function insertHorizontalRule(state: TextState): TextEdit {
  const { value } = state;
  const pos = Math.max(state.selectionStart, state.selectionEnd);
  const { start, end } = lineBoundsAt(value, pos);
  const lineText = value.slice(start, end);
  const insert = lineText.trim() === '' ? '---\n' : '\n---\n';
  const caret = end + insert.length;
  return { from: end, to: end, insert, selection: { start: caret, end: caret } };
}

/** 太字。日本語で確実に効く `**` を使う。 */
export const BOLD_MARKER = '**';
/** 斜体。`_` は CommonMark の flanking 規則により日本語の語中で効かないため `*` を使う。 */
export const ITALIC_MARKER = '*';
export const CODE_MARKER = '`';

/**
 * 選択範囲を記号で囲む／外す（太字・斜体・インラインコード）。
 *
 * - 選択があり、既に囲まれていれば外す（選択の内側・外側のどちらでも判定する）
 * - 選択がなければ記号を 2 つ挿入し、その間へカーソルを置く
 */
export function toggleInlineWrap(state: TextState, marker: string): TextEdit {
  const { value } = state;
  const from = Math.min(state.selectionStart, state.selectionEnd);
  const to = Math.max(state.selectionStart, state.selectionEnd);
  const selected = value.slice(from, to);
  const len = marker.length;

  // 斜体（`*`）で太字（`**`）を誤って外さないための番人。
  const looksLikeStrongerMarker =
    marker === ITALIC_MARKER && selected.startsWith(BOLD_MARKER) && selected.endsWith(BOLD_MARKER);

  if (
    !looksLikeStrongerMarker &&
    selected.length >= len * 2 &&
    selected.startsWith(marker) &&
    selected.endsWith(marker)
  ) {
    const inner = selected.slice(len, selected.length - len);
    return { from, to, insert: inner, selection: { start: from, end: from + inner.length } };
  }

  const before = value.slice(Math.max(0, from - len), from);
  const after = value.slice(to, to + len);
  if (from !== to && before === marker && after === marker) {
    const start = from - len;
    return {
      from: start,
      to: to + len,
      insert: selected,
      selection: { start, end: start + selected.length },
    };
  }

  if (from === to) {
    const caret = from + len;
    return { from, to, insert: marker + marker, selection: { start: caret, end: caret } };
  }

  return {
    from,
    to,
    insert: marker + selected + marker,
    selection: { start: from + len, end: from + len + selected.length },
  };
}

/** カーソル位置へ文字列を挿入する編集を作る（画像参照の挿入などに使う）。 */
export function insertAtSelection(state: TextState, text: string): TextEdit {
  const from = Math.min(state.selectionStart, state.selectionEnd);
  const to = Math.max(state.selectionStart, state.selectionEnd);
  const caret = from + text.length;
  return { from, to, insert: text, selection: { start: caret, end: caret } };
}

/**
 * 行頭の見出し記号・引用記号・リスト記号を表示から落とす。
 *
 * `deriveDisplayTitle` と `deriveSummaryText` の両方が使う。
 * 記法の解釈を 2 箇所に書くと片方だけ直る事故が起きるので、ここに 1 つだけ置く。
 */
function stripLineMarkers(line: string): string {
  return line
    .trim()
    .replace(/^#{1,6}\s+/, '')
    .replace(/^>\s*/, '')
    .replace(/^([-*+]|\d{1,9}[.)])\s+/, '')
    .trim();
}

/** サマリーとして DOM へ渡す最大文字数。表示行数の制御は CSS 側が行う。 */
const SUMMARY_MAX_LENGTH = 200;

/**
 * タイトルを表示する場面での表示値（docs/01-requirements.md §5.2 / ADR-031）。
 *
 * タイトル未入力なら「無題」。**本文で代替しない。** 代替すると、付けていないタイトルが
 * 付いているように見え、一覧のように本文サマリーを併記する画面では同じ文字列が二重に出る。
 *
 * 「無題」は欠落ではなく**正当な状態**（§4.1: 無題・無カテゴリなら日記断片）。
 * 埋めるべき欄のようには扱わない。
 *
 * **本文を引数に取らないのは意図的。** 受け取れる形にすると、また流用を書けてしまう。
 * 内容の手がかりは呼び出し側がサマリーや抜粋で別途出すこと（§5.2 の P0）。
 */
export function displayTitle(title: string | null): string {
  // trim した値ではなく元の文字列を返す。保存値を勝手に整形しない。
  return title !== null && title.trim() !== '' ? title : '無題';
}

/**
 * 一覧での代替タイトル（旧規則）。
 *
 * **ADR-031 でこの規則は廃止された。新しい呼び出し元を増やさないこと。**
 * タイトルを表示したい画面は `displayTitle` を使う。
 *
 * タイムラインだけが今も使っている。タイムラインはタイトルの表示が必須ではない
 * （カードを開けば本文がその場で読める）ため、「無題」を並べるのではなく
 * **タイトル行の扱いごと見直す**方針で、それまでの経過措置として残してある。
 * 見直しは T-008（`docs/tasks/T-008-timeline-title-row.md`）。
 * **T-008 が終わったらこの関数ごと削除する。**
 */
export function deriveDisplayTitle(title: string | null, body: string): string {
  if (title && title.trim() !== '') return title;
  for (const rawLine of body.split('\n')) {
    const stripped = stripLineMarkers(rawLine);
    if (stripped !== '') return stripped;
  }
  return '無題';
}

/**
 * 一覧に出す本文先頭のプレーンテキスト（docs/01-requirements.md §5.17）。
 *
 * **本文の最初の意味のある行から**出す。タイトル欄が本文を流用しなくなった（ADR-031）ので、
 * 「代替タイトルに使った行を飛ばす」必要がなくなった。
 *
 * タイトルが「無題」になる場面ほど、この行が唯一の手がかりになる（§5.2 の P0:
 * 無題と表示する場面では本文の手がかりを必ず併せて出す）。
 *
 * Markdown を描画せずプレーンテキストにするのは意図的（一覧に画像やコードブロックが
 * 入って行の高さが暴れると「素朴な一覧」でなくなるため）。
 */
export function deriveSummaryText(body: string): string {
  const parts: string[] = [];

  for (const rawLine of body.split('\n')) {
    const stripped = stripLineMarkers(rawLine);
    if (stripped === '') continue;
    parts.push(stripped);
    // 上限を超えたら以降の行を読む意味がない。
    if (parts.join(' ').length >= SUMMARY_MAX_LENGTH) break;
  }

  const joined = parts.join(' ');
  return joined.length > SUMMARY_MAX_LENGTH ? joined.slice(0, SUMMARY_MAX_LENGTH) : joined;
}
